import { ReactNode, useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * AppLayout component - Main layout wrapper for authenticated pages
 * Provides consistent structure with Sidebar and TopBar across all pages
 * 
 * Structure:
 * - Sidebar (left, 240px on desktop, overlay on mobile/tablet)
 * - MainContent (flexible width, max-width 1400px centered)
 *   - TopBar (user avatar and logout)
 *   - Page content (children)
 */
export const AppLayout = ({ children }: AppLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Handle responsive breakpoints
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      
      // On desktop, sidebar is always visible (not controlled by state)
      if (width >= 1024) {
        setSidebarOpen(false); // Not used on desktop
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close sidebar when clicking outside on mobile/tablet
  useEffect(() => {
    if ((isMobile || isTablet) && sidebarOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-sidebar]') && !target.closest('[data-sidebar-toggle]')) {
          setSidebarOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [sidebarOpen, isMobile, isTablet]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Overlay backdrop for mobile/tablet */}
      {(isMobile || isTablet) && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={handleSidebarClose}
          aria-hidden="true"
        />
      )}

      {/* Main Content Area */}
      <div 
        className="flex flex-col min-w-0 min-h-screen" 
        style={{ marginLeft: !isMobile && !isTablet ? '64px' : '0' }}
      >
        {/* TopBar */}
        <TopBar
          onSidebarToggle={handleSidebarToggle}
          showToggle={isMobile || isTablet}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="w-full px-6 py-8">
            <div className="w-full">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

